import React from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { X, ShoppingCart, Trash2, Plus, Minus, ArrowRight, ShoppingBag, Globe2, ShieldCheck, Truck } from 'lucide-react';
import { useCart, useCurrency } from '../../contexts/SiteContexts';
import { cn } from '../../lib/utils';

interface CartSidebarProps {
  instruments?: any[];
}

export const CartSidebar: React.FC<CartSidebarProps> = ({ instruments }) => {
  const { cart, removeFromCart, updateQuantity, total, isCartOpen, setIsCartOpen, setIsCheckoutOpen } = useCart();
  const { formatPrice, formatAllPrices } = useCurrency();

  const totalAll = formatAllPrices(total);

  return (
    <AnimatePresence>
      {isCartOpen && (
        <div className="fixed inset-0 z-[300]">
          <motion.div 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setIsCartOpen(false)}
            className="absolute inset-0 bg-slate-950/60 backdrop-blur-md"
          />
          
          <motion.div 
            initial={{ x: '100%' }}
            animate={{ x: 0 }}
            exit={{ x: '100%' }}
            transition={{ type: 'spring', damping: 25, stiffness: 200 }}
            className="absolute right-0 top-0 bottom-0 w-full max-w-lg bg-white shadow-2xl flex flex-col"
          >
            {/* Header */}
            <div className="p-6 sm:p-8 border-b border-slate-100 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="p-2.5 bg-indigo-50 text-indigo-600 rounded-2xl">
                  <ShoppingBag size={22} />
                </div>
                <div>
                  <h2 className="text-xl font-black text-slate-900 uppercase tracking-tight">Your Cart</h2>
                  <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">
                    {cart.reduce((s, i) => s + i.quantity, 0)} Items Selected
                  </p>
                </div>
              </div>
              <button 
                onClick={() => setIsCartOpen(false)} 
                className="p-2.5 text-slate-400 hover:text-slate-950 hover:bg-slate-50 rounded-2xl transition-all"
              >
                <X size={20} />
              </button>
            </div>

            {/* Cart Items */}
            <div className="flex-1 overflow-y-auto p-6 sm:p-8 custom-scrollbar">
              {cart.length === 0 ? (
                <div className="h-full flex flex-col items-center justify-center text-center py-12">
                  <div className="w-20 h-20 bg-slate-50 rounded-3xl flex items-center justify-center mb-6 border border-slate-100">
                    <ShoppingCart className="text-slate-300" size={36} />
                  </div>
                  <p className="text-sm font-black text-slate-400 uppercase tracking-widest">Cart is empty</p>
                  <p className="text-xs text-slate-400 mt-1 max-w-xs">Explore our German-grade surgical instruments catalog</p>
                  <button 
                    onClick={() => setIsCartOpen(false)}
                    className="mt-6 px-6 py-3 bg-indigo-600 text-white rounded-2xl font-black text-xs uppercase tracking-widest hover:bg-slate-900 transition-all shadow-lg shadow-indigo-100"
                  >
                    Start Shopping
                  </button>
                </div>
              ) : (
                <div className="space-y-4">
                  {cart.map((item) => {
                    const itemTotal = (item.discountPrice || item.price) * item.quantity;
                    const itemPrices = formatAllPrices(itemTotal);
                    const singlePrice = formatAllPrices(item.discountPrice || item.price);

                    return (
                      <div key={item.id} className="p-4 bg-slate-50 rounded-3xl border border-slate-100 hover:border-indigo-200 transition-all group">
                        <div className="flex gap-4">
                          <div className="w-20 h-20 bg-white rounded-2xl flex-shrink-0 p-2 border border-slate-100 flex items-center justify-center">
                            <img 
                              src={item.imageUrl || 'https://images.unsplash.com/photo-1584362917165-526a968579e8?auto=format&fit=crop&q=80&w=300'} 
                              alt={item.name} 
                              className="w-full h-full object-contain"
                              referrerPolicy="no-referrer"
                            />
                          </div>
                          
                          <div className="flex-1 min-w-0">
                            <div className="flex items-start justify-between gap-2">
                              <div>
                                <span className="text-[9px] font-black uppercase tracking-widest text-indigo-600">
                                  {item.category || 'Surgical'}
                                </span>
                                <h4 className="text-xs font-black text-slate-900 uppercase tracking-tight line-clamp-1">
                                  {item.name}
                                </h4>
                                {item.tierLabel && (
                                  <div className="mt-0.5">
                                    <span className="inline-flex items-center px-2 py-0.5 bg-indigo-50 text-indigo-700 text-[8px] font-black rounded-md uppercase tracking-wider border border-indigo-100">
                                      {item.tierLabel}
                                    </span>
                                  </div>
                                )}
                                <p className="text-[10px] text-slate-400 font-mono">
                                  SKU: {item.code || item.id?.slice(0, 8)}
                                </p>
                              </div>

                              <button 
                                onClick={() => removeFromCart(item.id)}
                                className="p-1.5 text-slate-300 hover:text-red-500 rounded-lg hover:bg-white transition-colors"
                                title="Remove item"
                              >
                                <Trash2 size={15} />
                              </button>
                            </div>

                            {/* Multi-Currency Price Grid for this item */}
                            <div className="mt-2.5 p-2 bg-white rounded-xl border border-slate-100">
                              <div className="flex items-center justify-between text-xs">
                                <span className="font-black text-slate-900">{itemPrices.usd}</span>
                                <span className="font-bold text-indigo-600">{itemPrices.pkr}</span>
                              </div>
                              <div className="flex items-center justify-between text-[10px] text-slate-400 font-semibold mt-0.5 pt-1 border-t border-slate-50">
                                <span>{itemPrices.cny}</span>
                                <span>{itemPrices.eur}</span>
                              </div>
                            </div>

                            {/* Quantity Controls */}
                            <div className="flex items-center justify-between mt-3">
                              <span className="text-[10px] font-bold text-slate-400">
                                {singlePrice.usd}/unit
                              </span>

                              <div className="flex items-center bg-white border border-slate-200 rounded-xl overflow-hidden shadow-sm">
                                <button 
                                  onClick={() => updateQuantity(item.id, item.quantity - 1)}
                                  className="p-1.5 hover:bg-slate-50 text-slate-500 active:scale-95"
                                >
                                  <Minus size={13} />
                                </button>
                                <span className="w-8 text-center text-xs font-black text-slate-900">{item.quantity}</span>
                                <button 
                                  onClick={() => updateQuantity(item.id, item.quantity + 1)}
                                  className="p-1.5 hover:bg-slate-50 text-slate-500 active:scale-95"
                                >
                                  <Plus size={13} />
                                </button>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Cart Footer with 4-Currency Display & Checkout button */}
            {cart.length > 0 && (
              <div className="p-6 sm:p-8 border-t border-slate-100 bg-slate-50/70 space-y-5">
                {/* 4-Currency Breakdown Table */}
                <div className="p-4 bg-white rounded-2xl border border-slate-200 shadow-sm space-y-3">
                  <div className="flex items-center justify-between text-xs font-black uppercase tracking-wider text-slate-500 pb-2 border-b border-slate-100">
                    <span className="flex items-center gap-1.5">
                      <Globe2 size={14} className="text-indigo-600" /> Multi-Currency Subtotal
                    </span>
                    <span className="text-[10px] text-emerald-600 bg-emerald-50 px-2 py-0.5 rounded-full font-bold">
                      Live Rates
                    </span>
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <div className="p-2.5 bg-slate-50 rounded-xl border border-slate-100">
                      <p className="text-[9px] font-black text-slate-400 uppercase">US Dollar (USD)</p>
                      <p className="text-base font-black text-slate-900">{totalAll.usd}</p>
                    </div>

                    <div className="p-2.5 bg-indigo-50/50 rounded-xl border border-indigo-100">
                      <p className="text-[9px] font-black text-indigo-500 uppercase">Pakistani Rupee (PKR)</p>
                      <p className="text-base font-black text-indigo-700">{totalAll.pkr}</p>
                    </div>

                    <div className="p-2.5 bg-amber-50/50 rounded-xl border border-amber-100">
                      <p className="text-[9px] font-black text-amber-600 uppercase">Chinese Yuan (CNY)</p>
                      <p className="text-sm font-black text-amber-800">{totalAll.cny}</p>
                    </div>

                    <div className="p-2.5 bg-purple-50/50 rounded-xl border border-purple-100">
                      <p className="text-[9px] font-black text-purple-600 uppercase">Euro Zone (EUR)</p>
                      <p className="text-sm font-black text-purple-800">{totalAll.eur}</p>
                    </div>
                  </div>
                </div>

                <button 
                  onClick={() => {
                    setIsCheckoutOpen(true);
                    setIsCartOpen(false);
                  }}
                  className="w-full py-5 bg-indigo-600 text-white rounded-2xl font-black text-xs uppercase tracking-widest hover:bg-slate-950 transition-all shadow-xl shadow-indigo-100 flex items-center justify-center gap-3 active:scale-[0.99]"
                >
                  Proceed to Checkout & Route Map
                  <ArrowRight size={18} />
                </button>
              </div>
            )}
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
};
